import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { loginUser, registerUser } from '../api'
import { KM_VARS, KM_BASE } from './km-styles'

const API = process.env.REACT_APP_API_URL;

// ══════════════════════════════════════════════════════════
// EyeIcon — outside Account to avoid re-creation on render
// ══════════════════════════════════════════════════════════
const EyeIcon = ({ open }) => open ? (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ══════════════════════════════════════════════════════════
// ForgotPasswordModal — outside Account to prevent blink
// ══════════════════════════════════════════════════════════
const ForgotPasswordModal = ({
  fpStep, fpEmail, setFpEmail,
  fpOtp, fpError, fpMsg, fpLoading,
  fpResendTimer, fpShowPw, fpShowConfirm,
  fpNewPw, fpConfirmPw,
  setFpNewPw, setFpConfirmPw,
  setFpShowPw, setFpShowConfirm, setFpError,
  setFpStep, setFpOtp,
  handleFpSendOtp, handleFpVerifyOtp,
  handleFpReset, handleFpResend,
  handleOtpChange, handleOtpKeyDown, handleOtpPaste,
  otpRefs, resetFp,
}) => (
  <div className="fp-backdrop" onClick={(e) => { if (e.target.classList.contains('fp-backdrop')) resetFp(); }}>
    <div className="fp-modal km-card">

      {/* Header */}
      <div className="km-card-hd" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="acc-hd-label">
          <i className={`ph-bold ${fpStep === 'otp' ? 'ph-shield-check' : fpStep === 'reset' ? 'ph-lock-key' : 'ph-envelope'}`}></i>
          {fpStep === 'email' && ' Forgot Password'}
          {fpStep === 'otp'   && ' Verify OTP'}
          {fpStep === 'reset' && ' New Password'}
          {fpStep === 'done'  && ' All Done!'}
        </span>
        <button onClick={resetFp} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>
          <i className="ph ph-x" />
        </button>
      </div>

      <div className="acc-body">

        {/* ── Step: email ── */}
        {fpStep === 'email' && (
          <form onSubmit={handleFpSendOtp}>
            <p style={{ fontSize: 13, color: 'var(--km-txt2)', marginBottom: 20, lineHeight: 1.6 }}>
              Enter the email address linked to your account. We'll send a 6-digit OTP to reset your password.
            </p>
            <div className="acc-field">
              <label className="acc-lbl">Email address <span className="acc-req">*</span></label>
              <input
                type="email" className="acc-input" placeholder="Enter your registered email"
                required autoFocus value={fpEmail}
                onChange={e => { setFpEmail(e.target.value); setFpError(''); }}
              />
            </div>
            {fpError && <div className="fp-error">{fpError}</div>}
            <button type="submit" className="km-btn-primary acc-submit-btn" disabled={fpLoading}>
              {fpLoading ? <><span className="acc-spin" /> Sending OTP…</> : <>Send OTP <i className="ph ph-paper-plane-tilt" /></>}
            </button>
          </form>
        )}

        {/* ── Step: otp ── */}
        {fpStep === 'otp' && (
          <form onSubmit={handleFpVerifyOtp}>
            {fpMsg && <div className="fp-success">{fpMsg}</div>}
            <p style={{ fontSize: 13, color: 'var(--km-txt2)', marginBottom: 20, lineHeight: 1.6 }}>
              Enter the 6-digit OTP sent to <strong style={{ color: 'var(--km-blue-deep)' }}>{fpEmail}</strong>
            </p>

            <div className="fp-otp-row" onPaste={handleOtpPaste}>
              {fpOtp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={el => otpRefs.current[idx] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`fp-otp-box${digit ? ' filled' : ''}`}
                  value={digit}
                  onChange={e => handleOtpChange(idx, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(idx, e)}
                />
              ))}
            </div>

            {fpError && <div className="fp-error">{fpError}</div>}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 20px', fontSize: 12 }}>
              <span style={{ color: 'var(--km-txt3)' }}>Didn't receive it?</span>
              <button
                type="button"
                onClick={handleFpResend}
                disabled={fpResendTimer > 0 || fpLoading}
                style={{ background: 'none', border: 'none', cursor: fpResendTimer > 0 ? 'not-allowed' : 'pointer', color: fpResendTimer > 0 ? 'var(--km-txt3)' : 'var(--km-orange)', fontWeight: 700, fontSize: 12, padding: 0 }}
              >
                {fpResendTimer > 0 ? `Resend in ${fpResendTimer}s` : 'Resend OTP'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => { setFpStep('email'); setFpOtp(['','','','','','']); setFpError(''); }}
                className="acc-cancel-btn" style={{ flex: 1 }}
              >← Back</button>
              <button type="submit" className="km-btn-primary acc-submit-btn" disabled={fpLoading} style={{ flex: 2 }}>
                {fpLoading ? <><span className="acc-spin" /> Verifying…</> : <>Verify OTP <i className="ph ph-shield-check" /></>}
              </button>
            </div>
          </form>
        )}

        {/* ── Step: reset ── */}
        {fpStep === 'reset' && (
          <form onSubmit={handleFpReset}>
            {fpMsg && <div className="fp-success">{fpMsg}</div>}
            <p style={{ fontSize: 13, color: 'var(--km-txt2)', marginBottom: 20, lineHeight: 1.6 }}>
              Choose a strong new password for <strong style={{ color: 'var(--km-blue-deep)' }}>{fpEmail}</strong>
            </p>

            <div className="acc-field">
              <label className="acc-lbl">New Password <span className="acc-req">*</span></label>
              <div className="acc-pw-wrap">
                <input
                  type={fpShowPw ? 'text' : 'password'} className="acc-input"
                  placeholder="At least 6 characters" required style={{ paddingRight: 46 }}
                  value={fpNewPw} onChange={e => { setFpNewPw(e.target.value); setFpError(''); }}
                />
                <button type="button" className="acc-eye" onClick={() => setFpShowPw(p => !p)}>
                  <EyeIcon open={fpShowPw} />
                </button>
              </div>
              {fpNewPw.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ height: 4, borderRadius: 2, background: '#eee', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2, transition: 'width .3s, background .3s',
                      width: fpNewPw.length < 6 ? '25%' : fpNewPw.length < 10 ? '60%' : '100%',
                      background: fpNewPw.length < 6 ? '#EF4444' : fpNewPw.length < 10 ? '#F59E0B' : '#10B981',
                    }} />
                  </div>
                  <span style={{ fontSize: 10, color: fpNewPw.length < 6 ? '#EF4444' : fpNewPw.length < 10 ? '#F59E0B' : '#10B981', fontWeight: 700, marginTop: 3, display: 'block' }}>
                    {fpNewPw.length < 6 ? 'Too short' : fpNewPw.length < 10 ? 'Fair' : 'Strong'}
                  </span>
                </div>
              )}
            </div>

            <div className="acc-field">
              <label className="acc-lbl">Confirm Password <span className="acc-req">*</span></label>
              <div className="acc-pw-wrap">
                <input
                  type={fpShowConfirm ? 'text' : 'password'} className="acc-input"
                  placeholder="Re-enter new password" required style={{ paddingRight: 46 }}
                  value={fpConfirmPw} onChange={e => { setFpConfirmPw(e.target.value); setFpError(''); }}
                />
                <button type="button" className="acc-eye" onClick={() => setFpShowConfirm(p => !p)}>
                  <EyeIcon open={fpShowConfirm} />
                </button>
              </div>
              {fpConfirmPw && fpNewPw !== fpConfirmPw && (
                <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600, marginTop: 4, display: 'block' }}>
                  Passwords don't match
                </span>
              )}
              {fpConfirmPw && fpNewPw === fpConfirmPw && (
                <span style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginTop: 4, display: 'block' }}>
                  ✓ Passwords match
                </span>
              )}
            </div>

            {fpError && <div className="fp-error">{fpError}</div>}

            <button type="submit" className="km-btn-primary acc-submit-btn" disabled={fpLoading}>
              {fpLoading ? <><span className="acc-spin" /> Resetting…</> : <>Reset Password <i className="ph ph-lock-key" /></>}
            </button>
          </form>
        )}

        {/* ── Step: done ── */}
        {fpStep === 'done' && (
          <div style={{ textAlign: 'center', padding: '10px 0 6px' }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>✅</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--km-blue-deep)', marginBottom: 8 }}>
              Password Reset!
            </h3>
            <p style={{ fontSize: 13, color: 'var(--km-txt2)', marginBottom: 24, lineHeight: 1.6 }}>
              Your password has been updated. You can now log in with your new password.
            </p>
            <button className="km-btn-primary acc-submit-btn" onClick={resetFp}>
              Back to Login <i className="ph ph-sign-in" />
            </button>
          </div>
        )}

      </div>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════
// MAIN ACCOUNT COMPONENT
// ══════════════════════════════════════════════════════════
const Account = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn]     = useState(false);
  const [customer,   setCustomer]       = useState(null);
  const [showLoginPw,  setShowLoginPw]  = useState(false);
  const [showRegPw,    setShowRegPw]    = useState(false);
  const [loginForm,    setLoginForm]    = useState({ email: '', password: '' });
  const [regForm,      setRegForm]      = useState({ username: '', email: '', phone: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [regLoading,   setRegLoading]   = useState(false);

  const [editMode,    setEditMode]    = useState(false);
  const [editForm,    setEditForm]    = useState({ username: '', email: '', phone: '' });
  const [editLoading, setEditLoading] = useState(false);

  // ── Forgot password state ──────────────────────────────
  const [fpStep,        setFpStep]        = useState('idle');
  const [fpEmail,       setFpEmail]       = useState('');
  const [fpOtp,         setFpOtp]         = useState(['', '', '', '', '', '']);
  const [fpNewPw,       setFpNewPw]       = useState('');
  const [fpConfirmPw,   setFpConfirmPw]   = useState('');
  const [fpLoading,     setFpLoading]     = useState(false);
  const [fpError,       setFpError]       = useState('');
  const [fpMsg,         setFpMsg]         = useState('');
  const [fpShowPw,      setFpShowPw]      = useState(false);
  const [fpShowConfirm, setFpShowConfirm] = useState(false);
  const [fpResendTimer, setFpResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (fpResendTimer <= 0) return;
    const t = setTimeout(() => setFpResendTimer(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [fpResendTimer]);

  useEffect(() => {
    const raw = localStorage.getItem('customer');
    if (raw) {
      const user = JSON.parse(raw);
      setCustomer(user);
      setIsLoggedIn(true);
      setEditForm({ username: user.username || '', email: user.email || '', phone: user.phone || '' });
    }
  }, []);

  // ── Reset forgot password state ───────────────────────
  const resetFp = () => {
    setFpStep('idle');
    setFpEmail('');
    setFpOtp(['', '', '', '', '', '']);
    setFpNewPw('');
    setFpConfirmPw('');
    setFpError('');
    setFpMsg('');
    setFpLoading(false);
    setFpResendTimer(0);
  };

  // ── Step 1: Send OTP ──────────────────────────────────
  const handleFpSendOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/user/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: fpEmail }),
      });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error. Please check API configuration.');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Email not found');
      setFpMsg(`OTP sent to ${fpEmail}`);
      setFpStep('otp');
      setFpResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  // ── OTP input handlers ────────────────────────────────
  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...fpOtp];
    next[idx] = val;
    setFpOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !fpOtp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowLeft'  && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next   = [...fpOtp];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setFpOtp(next);
    const lastFilled = Math.min(pasted.length, 5);
    otpRefs.current[lastFilled]?.focus();
  };

  // ── Step 2: Verify OTP ────────────────────────────────
  const handleFpVerifyOtp = async (e) => {
    e.preventDefault();
    const otp = fpOtp.join('');
    if (otp.length < 6) { setFpError('Please enter the full 6-digit OTP.'); return; }
    setFpError('');
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/user/verify-otp`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: fpEmail, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Invalid OTP');
      setFpMsg('OTP verified! Set your new password.');
      setFpStep('reset');
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────
  const handleFpResend = async () => {
    if (fpResendTimer > 0) return;
    setFpError('');
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/user/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: fpEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to resend');
      setFpMsg('New OTP sent!');
      setFpOtp(['', '', '', '', '', '']);
      setFpResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  // ── Step 3: Reset password ────────────────────────────
  const handleFpReset = async (e) => {
    e.preventDefault();
    setFpError('');
    if (fpNewPw.length < 6) { setFpError('Password must be at least 6 characters.'); return; }
    if (fpNewPw !== fpConfirmPw) { setFpError('Passwords do not match.'); return; }
    setFpLoading(true);
    try {
      const res = await fetch(`${API}/user/reset-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: fpEmail, otp: fpOtp.join(''), newPassword: fpNewPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Reset failed');
      setFpStep('done');
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  // ── Login ─────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res  = await loginUser(loginForm);
      const user = res.data.user;

      // ✅ FIX: Get correct customer id from customers table by email
      try {
        const custRes  = await fetch(`${API}/customers/by-email?email=${user.email}`);
        const custData = await custRes.json();
        if (custData?.id) user.id = custData.id;
      } catch (e) {
        console.warn('Could not fetch customer id:', e);
      }

      localStorage.setItem('customer', JSON.stringify(user));
      setCustomer(user);
      setIsLoggedIn(true);
      setEditForm({ username: user.username || '', email: user.email || '', phone: user.phone || '' });
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.message || 'Login failed.');
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Register ──────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      const res  = await registerUser(regForm);
      const user = res.data.user;
      const customerRes = await fetch(`${API}/customers/add`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: user.username, phone: user.phone || null, email: user.email, status: 'Active' })
      });
      if (!customerRes.ok) {
        const errData = await customerRes.json();
        throw new Error(errData.error || 'Customer save failed');
      }
      alert('Registration Successful! Now you can Login.');
      setRegForm({ username: '', email: '', phone: '', password: '' });
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Registration failed.';
      alert(msg);
    } finally {
      setRegLoading(false);
    }
  };

  // ── Logout ────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('customer');
    setIsLoggedIn(false);
    setCustomer(null);
    navigate('/');
  };
const handleEditSave = async (e) => {
  e.preventDefault();
  setEditLoading(true);

  const payload = {
    id: customer.id || customer._id,   // ← handle MongoDB _id
    username: editForm.username,
    email: editForm.email,
    phone: editForm.phone,
  };

  console.log('Sending payload:', payload); // ← check browser console

  try {
    const response = await fetch(`${API}/user/update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    console.log('Server response:', data); // ← see exact error message

    if (!response.ok) throw new Error(data.error || data.message || 'Update failed');

    const updated = { ...customer, ...editForm };
    localStorage.setItem('customer', JSON.stringify(updated));
    setCustomer(updated);
    setEditMode(false);
    alert('Profile updated successfully!');
  } catch (err) {
    alert(err.message); // ← now shows actual server error
  } finally {
    setEditLoading(false);
  }
};

  const getInitials = (name = '') =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // ══════════════════════════════════════════════════════
  // LOGGED IN VIEW
  // ══════════════════════════════════════════════════════
  if (isLoggedIn && customer) {
    return (
      <section className="km-page">
        <style>{KM_VARS + KM_BASE + ACC_STYLES}</style>
        <div className="km-full">

          <div className="km-ph" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1><i className="ph ph-user-circle"></i> My Account</h1>
              <p>Manage your profile &amp; orders</p>
            </div>
            <button onClick={handleLogout} className="acc-logout-btn">
              <i className="ph-bold ph-sign-out"></i> Logout
            </button>
          </div>

          <div className="profile-layout">
            <div className="profile-sidebar">
              <div className="profile-avatar-card km-card">
                <div className="profile-avatar">{getInitials(customer.username)}</div>
                <div className="profile-name">{customer.username}</div>
                <div className="profile-email">{customer.email}</div>
                {!editMode && (
                  <button className="acc-edit-btn" onClick={() => setEditMode(true)}>
                    <i className="ph ph-pencil-simple"></i> Edit Profile
                  </button>
                )}
              </div>
              <div className="km-card profile-quicklinks">
                <div className="pql-title">Quick Links</div>
                <Link to="/orders"   className="pql-item"><i className="ph ph-package"></i> My Orders</Link>
                <Link to="/wishlist" className="pql-item"><i className="ph ph-heart"></i> Wishlist</Link>
                <Link to="/cart"     className="pql-item"><i className="ph ph-shopping-cart-simple"></i> Cart</Link>
              </div>
            </div>

            <div className="profile-main">
              {!editMode ? (
                <div className="km-card profile-info-card">
                  <div className="km-card-hd">
                    <span className="acc-hd-label"><i className="ph-bold ph-identification-card"></i> Profile Details</span>
                  </div>
                  <div className="acc-body">
                    <div className="profile-field-row">
                      <span className="pf-label">Username</span>
                      <span className="pf-value">{customer.username}</span>
                    </div>
                    <div className="profile-field-row">
                      <span className="pf-label">Email</span>
                      <span className="pf-value">{customer.email}</span>
                    </div>
                    <div className="profile-field-row">
                      <span className="pf-label">Phone</span>
                      <span className="pf-value">{customer.phone || <span className="pf-empty">Not added</span>}</span>
                    </div>
                    <div className="profile-field-row">
                      <span className="pf-label">Account Status</span>
                      <span className="pf-badge">Active</span>
                    </div>
                  </div>
                </div>
              ) : (
                <form className="km-card profile-info-card" onSubmit={handleEditSave}>
                  <div className="km-card-hd" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span className="acc-hd-label"><i className="ph-bold ph-pencil-simple"></i> Edit Profile</span>
                    <button type="button" onClick={() => setEditMode(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 20 }}>
                      <i className="ph ph-x"></i>
                    </button>
                  </div>
                  <div className="acc-body">
                    <div className="acc-field">
                      <label className="acc-lbl">Username <span className="acc-req">*</span></label>
                      <input type="text" className="acc-input" required value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} />
                    </div>
                    <div className="acc-field">
                      <label className="acc-lbl">Email <span className="acc-req">*</span></label>
                      <input type="email" className="acc-input" required value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                    </div>
                    <div className="acc-field">
                      <label className="acc-lbl">Phone</label>
                      <input type="tel" className="acc-input" placeholder="Enter phone number" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button type="submit" className="km-btn-primary acc-submit-btn" disabled={editLoading} style={{ flex: 1 }}>
                        {editLoading ? <><span className="acc-spin" /> Saving…</> : <><i className="ph ph-floppy-disk"></i> Save Changes</>}
                      </button>
                      <button type="button" className="acc-cancel-btn" onClick={() => setEditMode(false)} style={{ flex: 1 }}>Cancel</button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ══════════════════════════════════════════════════════
  // NOT LOGGED IN VIEW
  // ══════════════════════════════════════════════════════
  return (
    <section className="km-page">
      <style>{KM_VARS + KM_BASE + ACC_STYLES}</style>

      {/* Forgot password modal */}
      {fpStep !== 'idle' && (
        <ForgotPasswordModal
          fpStep={fpStep}
          fpEmail={fpEmail}
          setFpEmail={setFpEmail}
          fpOtp={fpOtp}
          fpError={fpError}
          fpMsg={fpMsg}
          fpLoading={fpLoading}
          fpResendTimer={fpResendTimer}
          fpShowPw={fpShowPw}
          fpShowConfirm={fpShowConfirm}
          fpNewPw={fpNewPw}
          fpConfirmPw={fpConfirmPw}
          setFpNewPw={setFpNewPw}
          setFpConfirmPw={setFpConfirmPw}
          setFpShowPw={setFpShowPw}
          setFpShowConfirm={setFpShowConfirm}
          setFpError={setFpError}
          setFpStep={setFpStep}
          setFpOtp={setFpOtp}
          handleFpSendOtp={handleFpSendOtp}
          handleFpVerifyOtp={handleFpVerifyOtp}
          handleFpReset={handleFpReset}
          handleFpResend={handleFpResend}
          handleOtpChange={handleOtpChange}
          handleOtpKeyDown={handleOtpKeyDown}
          handleOtpPaste={handleOtpPaste}
          otpRefs={otpRefs}
          resetFp={resetFp}
        />
      )}

      <div className="km-full">
        <div className="km-ph">
          <h1><i className="ph ph-user-circle"></i> My Account</h1>
          <p>Login or create a new account</p>
        </div>

        <div className="acc-grid">

          {/* ── Register ── */}
          <form className="km-card acc-form" onSubmit={handleRegister}>
            <div className="km-card-hd">
              <span className="acc-hd-label"><i className="ph-bold ph-user-plus"></i> Register</span>
            </div>
            <div className="acc-body">
              <div className="acc-field">
                <label className="acc-lbl">Username <span className="acc-req">*</span></label>
                <input type="text" className="acc-input" placeholder="Choose a username" required value={regForm.username} onChange={e => setRegForm({ ...regForm, username: e.target.value })} />
              </div>
              <div className="acc-field">
                <label className="acc-lbl">Email address <span className="acc-req">*</span></label>
                <input type="email" className="acc-input" placeholder="Enter your email" required value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })} />
              </div>
              <div className="acc-field">
                <label className="acc-lbl">Phone Number <span className="acc-req">*</span></label>
                <input type="tel" className="acc-input" placeholder="Enter 10-digit mobile number" required pattern="[0-9]{10}" title="Please enter a valid 10-digit phone number" value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} />
              </div>
              <div className="acc-field">
                <label className="acc-lbl">Password <span className="acc-req">*</span></label>
                <div className="acc-pw-wrap">
                  <input type={showRegPw ? 'text' : 'password'} className="acc-input" placeholder="Create a password" required style={{ paddingRight: 46 }} value={regForm.password} onChange={e => setRegForm({ ...regForm, password: e.target.value })} />
                  <button type="button" className="acc-eye" onClick={() => setShowRegPw(p => !p)}>
                    <EyeIcon open={showRegPw} />
                  </button>
                </div>
              </div>
              <div className="acc-privacy">
                Your personal data will be used to process your order and support your experience.{' '}
                <Link to="#" style={{ color: 'var(--km-orange)', fontWeight: 600 }}>privacy policy</Link>.
              </div>
              <button type="submit" className="km-btn-primary acc-submit-btn" disabled={regLoading}>
                {regLoading ? <><span className="acc-spin" /> Creating account…</> : <>Create Account</>}
              </button>
            </div>
          </form>

          {/* ── Login ── */}
          <form className="km-card acc-form" onSubmit={handleLogin}>
            <div className="km-card-hd">
              <span className="acc-hd-label"><i className="ph-bold ph-sign-in"></i> Login</span>
            </div>
            <div className="acc-body">
              <div className="acc-field">
                <label className="acc-lbl">Email <span className="acc-req">*</span></label>
                <input type="email" className="acc-input" placeholder="Enter your registered email" required value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} />
              </div>
              <div className="acc-field">
                <label className="acc-lbl">Password <span className="acc-req">*</span></label>
                <div className="acc-pw-wrap">
                  <input type={showLoginPw ? 'text' : 'password'} className="acc-input" placeholder="Enter password" required style={{ paddingRight: 46 }} value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} />
                  <button type="button" className="acc-eye" onClick={() => setShowLoginPw(p => !p)}>
                    <EyeIcon open={showLoginPw} />
                  </button>
                </div>
              </div>

              <div className="acc-meta-row">
                <label className="acc-remember">
                  <input type="checkbox" id="remember" className="acc-check" />
                  <span className="acc-checkmark" />
                  <span className="acc-remember-txt">Remember me</span>
                </label>
                <button
                  type="button"
                  className="acc-forgot"
                  onClick={() => { setFpEmail(loginForm.email); setFpStep('email'); setFpError(''); }}
                >
                  Forgot password?
                </button>
              </div>

              <div className="acc-trust">
                <span><span className="acc-trust-dot" />Secure Login</span>
                <span><span className="acc-trust-dot" />Data Encrypted</span>
              </div>

              <button type="submit" className="km-btn-primary acc-submit-btn" disabled={loginLoading}>
                {loginLoading ? <><span className="acc-spin" /> Logging in…</> : <>Log in</>}
              </button>
            </div>
          </form>

        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════
const ACC_STYLES = `
  .acc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
  @media(max-width:860px){ .acc-grid { grid-template-columns: 1fr; } }
  .acc-form { overflow: hidden; }
  .acc-hd-label { margin-right: auto; font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.55); letter-spacing: .1em; text-transform: uppercase; }
  .acc-body { padding: 28px 28px 32px; }
  .acc-field { margin-bottom: 18px; }
  .acc-lbl { display: block; font-size: 12px; font-weight: 700; color: var(--km-blue-deep); margin-bottom: 6px; letter-spacing: .02em; }
  .acc-req { color: var(--km-orange); margin-left: 2px; }
  .acc-input { width: 100%; padding: 11px 16px; border: 1px solid var(--km-bdr); border-radius: 10px; font-size: 13px; color: var(--km-txt); background: #fff; outline: none; transition: border-color .2s, box-shadow .2s; box-sizing: border-box; font-family: inherit; }
  .acc-input::placeholder { color: #bbb; }
  .acc-input:focus { border-color: var(--km-blue-mid); box-shadow: 0 0 0 3px rgba(26,82,168,.1); }
  .acc-pw-wrap { position: relative; }
  .acc-eye { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: var(--km-txt3); padding: 0; display: flex; align-items: center; transition: color .15s; }
  .acc-eye:hover { color: var(--km-blue-mid); }
  .acc-meta-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; flex-wrap: wrap; gap: 10px; }
  .acc-remember { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
  .acc-check { display: none; }
  .acc-checkmark { width: 17px; height: 17px; border: 2px solid var(--km-blue-mid); border-radius: 4px; background: #fff; display: flex; align-items: center; justify-content: center; position: relative; transition: background .15s; flex-shrink: 0; }
  .acc-check:checked + .acc-checkmark { background: var(--km-blue-mid); }
  .acc-check:checked + .acc-checkmark::after { content: '✓'; color: #fff; font-size: 10px; font-weight: 700; position: absolute; }
  .acc-remember-txt { font-size: 13px; color: var(--km-txt2); font-weight: 500; }
  .acc-forgot { font-size: 12px; color: var(--km-orange); font-weight: 700; text-decoration: none; background: none; border: none; cursor: pointer; padding: 0; font-family: inherit; }
  .acc-forgot:hover { text-decoration: underline; }
  .acc-trust { display: flex; gap: 18px; margin-bottom: 22px; padding: 10px 14px; background: var(--km-blue-xl); border-radius: 8px; border: 1px solid var(--km-bdr); }
  .acc-trust span { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--km-blue-mid); font-weight: 700; }
  .acc-trust-dot { width: 6px; height: 6px; background: var(--km-green); border-radius: 50%; display: inline-block; }
  .acc-privacy { background: var(--km-blue-xl); border: 0.5px solid var(--km-bdr); border-radius: 10px; padding: 12px 16px; font-size: 12px; color: var(--km-txt2); line-height: 1.65; margin-bottom: 24px; }
  .acc-submit-btn { width: 100%; justify-content: center; padding: 13px 24px; font-size: 13px; }
  .acc-submit-btn:disabled { opacity: .65; cursor: not-allowed; transform: none !important; }
  .acc-spin { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: km-spin .7s linear infinite; display: inline-block; }
  .acc-logout-btn { padding: 8px 16px; background: var(--km-orange); color: white; border: none; border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: opacity 0.2s; }
  .acc-logout-btn:hover { opacity: 0.9; }
  .profile-layout { display: grid; grid-template-columns: 260px 1fr; gap: 24px; align-items: start; }
  @media(max-width:860px){ .profile-layout { grid-template-columns: 1fr; } }
  .profile-avatar-card { padding: 28px 20px 24px; display: flex; flex-direction: column; align-items: center; gap: 10px; overflow: hidden; }
  .profile-avatar { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, var(--km-blue-mid), var(--km-orange)); color: #fff; font-size: 26px; font-weight: 700; display: flex; align-items: center; justify-content: center; letter-spacing: 1px; flex-shrink: 0; }
  .profile-name { font-size: 16px; font-weight: 700; color: var(--km-blue-deep); text-align: center; }
  .profile-email { font-size: 12px; color: var(--km-txt3); text-align: center; word-break: break-all; }
  .acc-edit-btn { margin-top: 6px; padding: 8px 18px; background: var(--km-blue-xl); border: 1px solid var(--km-bdr); border-radius: 8px; font-size: 13px; font-weight: 600; color: var(--km-blue-mid); cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all .2s; }
  .acc-edit-btn:hover { background: var(--km-blue-mid); color: #fff; }
  .acc-cancel-btn { padding: 13px 24px; font-size: 13px; font-weight: 600; background: #f5f5f5; border: 1px solid var(--km-bdr); border-radius: 10px; color: var(--km-txt2); cursor: pointer; transition: background .2s; font-family: inherit; }
  .acc-cancel-btn:hover { background: #ebebeb; }
  .profile-quicklinks { padding: 16px 20px; margin-top: 0; }
  .pql-title { font-size: 11px; font-weight: 700; color: var(--km-txt3); letter-spacing: .08em; text-transform: uppercase; margin-bottom: 10px; }
  .pql-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px; font-size: 13px; font-weight: 600; color: var(--km-txt); text-decoration: none; transition: all .2s; }
  .pql-item:hover { background: var(--km-blue-xl); color: var(--km-blue-mid); }
  .pql-item i { font-size: 16px; color: var(--km-blue-mid); }
  .profile-info-card { overflow: hidden; }
  .profile-field-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid var(--km-bdr); }
  .profile-field-row:last-child { border-bottom: none; }
  .pf-label { font-size: 12px; font-weight: 700; color: var(--km-txt3); text-transform: uppercase; letter-spacing: .06em; }
  .pf-value { font-size: 14px; color: var(--km-blue-deep); font-weight: 500; }
  .pf-empty { color: #bbb; font-style: italic; font-weight: 400; }
  .pf-badge { background: #e6f9ee; color: #1a7c43; font-size: 11px; font-weight: 700; padding: 4px 12px; border-radius: 20px; border: 1px solid #b2e8c8; }

  /* ── Forgot Password Modal ── */
  .fp-backdrop {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fp-fade-in .2s ease;
  }
  @keyframes fp-fade-in { from { opacity: 0; } to { opacity: 1; } }
  .fp-modal {
    width: 100%; max-width: 420px;
    overflow: hidden;
    animation: fp-slide-up .25s cubic-bezier(.4,0,.2,1);
  }
  @keyframes fp-slide-up { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .fp-otp-row { display: flex; gap: 8px; justify-content: center; margin-bottom: 8px; }
  .fp-otp-box { width: 44px; height: 52px; border: 2px solid var(--km-bdr); border-radius: 10px; text-align: center; font-size: 22px; font-weight: 700; color: var(--km-blue-deep); outline: none; transition: border-color .2s, box-shadow .2s; background: #fff; caret-color: var(--km-orange); font-family: inherit; }
  .fp-otp-box:focus { border-color: var(--km-blue-mid); box-shadow: 0 0 0 3px rgba(26,82,168,.12); }
  .fp-otp-box.filled { border-color: var(--km-orange); background: #FFF8F3; }
  .fp-error { background: #FEF2F2; border: 1px solid #FECACA; border-radius: 8px; padding: 10px 14px; font-size: 12px; font-weight: 600; color: #B91C1C; margin-bottom: 16px; }
  .fp-success { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 8px; padding: 10px 14px; font-size: 12px; font-weight: 600; color: #15803D; margin-bottom: 16px; }
`;

export default Account;